import { useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  Collapse,
  Divider,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import type { OrderDrawerIdentifier } from "../types";
import { useOrderCheckoutQuestions } from "../useOrderCheckoutQuestions";
import type { OrderCheckoutQuestionDto } from "@lib/ShiOneClient";

interface OrderDrawerCheckoutQuestionsProps {
  identifier: OrderDrawerIdentifier;
  enabled: boolean;
  disableGutters?: boolean;
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        py: 1,
      }}
    >
      <Typography variant="body1" color="text.secondary" sx={{ mr: 2 }}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        fontWeight="normal"
        sx={{ textAlign: "right", wordBreak: "break-word" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function CheckoutQuestionsContent({
  questions,
}: {
  questions: OrderCheckoutQuestionDto[];
}) {
  return (
    <Box sx={{ px: 2 }}>
      {questions.map((question, index) => (
        <Box key={`${question.questionText}-${index}`}>
          <DetailRow
            label={question.questionText ?? ""}
            value={question.answerValue ?? "—"}
          />
          {index < questions.length - 1 && (
            <Divider sx={{ borderColor: "divider" }} />
          )}
        </Box>
      ))}
    </Box>
  );
}

export function OrderDrawerCheckoutQuestions({
  identifier,
  enabled,
  disableGutters = false,
}: Readonly<OrderDrawerCheckoutQuestionsProps>) {
  const [expanded, setExpanded] = useState(true);
  const { checkoutQuestions } = useOrderCheckoutQuestions({
    identifier,
    enabled,
  });

  if (checkoutQuestions.length === 0) return null;

  return (
    <Card elevation={1} sx={{ mx: disableGutters ? 0 : 2, my: 0, borderRadius: 1 }}>
      <CardActionArea
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle1" sx={{ textTransform: "uppercase", fontWeight: "normal" }}>
          Checkout Questions
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
            color: "action.active",
          }}
        />
      </CardActionArea>
      <Collapse in={expanded}>
        <CheckoutQuestionsContent questions={checkoutQuestions} />
      </Collapse>
    </Card>
  );
}
